
import { Component, Vue } from 'vue-property-decorator';
import ScopesRepository from '@/services/Scopes';
import { Treeview } from '@/views/interfaces/vuetify/treeview.interface';
import { ScopeTree } from '@/services/Scopes/Models/ScopeTree';
import RoleRepository from '@/services/Roles';
import { RoleInterface } from '@/services/Roles/Interfaces';
import { ScopeInterface } from '@/services/Scopes/Interfaces';
import { WPXFormValue } from '@/components/templates/WPXForm';
import CreateRoleForm from '@/services/Auth/forms/CreateRoleForm';
import { DepartmentInterface } from '@/services/Departments/Interfaces';
import DepartmentRepository from '@/services/Departments';

@Component({
  inject: [
    'scopesRepository',
    'rolesRepository',
    'departmentRepository',
  ],
})
export default class CreateRole extends Vue {
  private readonly rolesRepository!: RoleRepository;
  private readonly scopesRepository!: ScopesRepository;
  private readonly departmentRepository!: DepartmentRepository;

  private loading: boolean = true;

  private form: WPXFormValue<CreateRoleForm> = new WPXFormValue(CreateRoleForm);

  private treeKey: number = 0;
  private openedBranches: string[] = [];

  private roles: RoleInterface[] = [];
  private departments: DepartmentInterface[] = [];

  private scopesLoading: boolean = false;
  private scopeTreeMap: ScopeTree[] = [];

  get scopes(): Treeview[] {
    return this.scopeTreeMap.map((tree: ScopeTree) => {
      return tree.toTreeView();
    });
  }

  private created(): void {
    Promise.all([
      this.initializeDepartments(),
      this.initializeScopes(),
      this.initializeRoles(),
    ]).then(() => {
      this.loading = false;
    });
  }

  private async initializeDepartments(): Promise<void> {
    try {
      const departments = await this.departmentRepository.getAllDepartments();

      departments.map((department: DepartmentInterface) => {
        this.departments.push(department);
      });
    } catch (err) {
      this.scopesLoading = false;
      this.$errorDialog.show('Could not retrieve departments.');
    }
  }

  private async initializeScopes(): Promise<void> {
    try {
      const scopes = await this.scopesRepository.getAllScopes();
      this.scopeTreeMap = this.scopesRepository.generateScopeTrees(scopes);
    } catch (err) {
      this.scopesLoading = false;
      this.$errorDialog.show('Could not retrieve scopes.');
    }
  }

  private async initializeRoles(): Promise<void> {
    try {
      const roles = await this.rolesRepository.getAllRoles();
      roles.filter((role) => role.name !== 'admin').map((role: RoleInterface) => {
        this.roles.push({
          id: role.id,
          name: role.name,
          scopes: role.scopes,
          permissions: [],
          department: role.department,
        });
      });

    } catch (err) {
      this.loading = false;
      this.$errorDialog.show('Could not retrieve roles.');
    }
  }

  private async setRoleExtension(change: string[]): Promise<void> {
    const extendedRoleIds = this.form.data.extendedRoles.map((role: RoleInterface) => role.id);
    change.forEach((roleId: string) => {
      if (extendedRoleIds.indexOf(roleId) === -1) {
        this.addExtendedRole(roleId);
      }
    });

    extendedRoleIds.forEach((roleId: string) => {
      if (change.indexOf(roleId) === -1) {
        this.removeExtendedRole(roleId);
      }
    });
  }

  private addExtendedRole(roleId: string): void {
    const newRole = this.roles.find((role: RoleInterface) => role.id === roleId);

    if (newRole === undefined) {
      this.$errorDialog.show(`Could not find role with id: ${roleId}`);
      return;
    }

    this.form.data.extendedRoles.push(newRole);
    this.updateRoleScopesStatus(newRole, true);
  }

  private removeExtendedRole(roleId: string): void {
    const oldRole = this.form.data.extendedRoles.find((role: RoleInterface) => role.id === roleId);

    if (oldRole === undefined) {
      this.$errorDialog.show(`Could not find role with id: ${roleId}`);
      return;
    }

    this.form.data.extendedRoles.splice(this.form.data.extendedRoles.indexOf(oldRole), 1);
    this.updateRoleScopesStatus(oldRole, false);
  }

  private updateRoleScopesStatus(role: RoleInterface, disabled: boolean, copy: boolean = false): void {
    role.scopes?.forEach((scope: ScopeInterface) => {
      const [service, func] = scope.name.split(':');
      const scopeTree = this.scopeTreeMap.find((tree: ScopeTree) => tree.name === service);

      if (scopeTree === undefined) {
        return;
      }

      this.openedBranches.push(scopeTree.id);

      const treeLeaf = scopeTree.getChild(func).getChild(scope.name);
      treeLeaf.disabled = disabled;
      if (disabled || copy) {
        this.form.data.selectedScopes.push(treeLeaf.id);
      } else {
        this.form.data.selectedScopes.splice(this.form.data.selectedScopes.indexOf(treeLeaf.id), 1);
        this.form.data.selectedScopes = JSON.parse(JSON.stringify(this.form.data.selectedScopes));
      }
    });
    this.treeKey++;
  }

  private async createRole(): Promise<void> {
    let extRoleScopes: string[] = [];
    this.form.data.extendedRoles.forEach((role: RoleInterface) => {
      if (!role.scopes) {
        return;
      }
      extRoleScopes = [...extRoleScopes, ...role.scopes.map((scope: ScopeInterface) => scope.id)];
    });

    const newScopes: string[] = [];
    this.form.data.selectedScopes.forEach((scope: string) => {
      if (extRoleScopes.indexOf(scope) === -1) {
        newScopes.push(scope);
      }
    });

    try {
      const newRole = {
        name: this.form.data.name,
        scopes: newScopes,
        extensions: this.form.data.extendedRoles.map((role: RoleInterface) => role.id),
        department: this.form.data.department,
      };
      await this.rolesRepository.createRole(newRole);
      this.loading = true;

      setTimeout(() => {
        Promise.all([
          this.initializeScopes(),
          this.initializeRoles(),
        ]).then(() => {
          this.loading = false;
        });
      }, 2000);

      this.$successDialog.show(`Successfully created role '${this.form.data.name}'`);
    } catch (err) {
      this.$errorDialog.show(err.response.data.message);
    }
  }

  private async syncScopes(): Promise<void> {
    this.scopesLoading = true;
    await this.scopesRepository.syncScopes();
    await this.initializeScopes();
    this.scopesLoading = false;
  }
}
